import { useEffect, useRef, useState } from "react";
import styles from "./GoogleMap.module.css";

interface OffenderData {
  sex_offender_name?: string;
  sex_offender_first_name?: string;
  sex_offender_middle_name?: string;
  sex_offender_last_name?: string;
  sex_offender_address_line1?: string;
  sex_offender_location?: string;
  sex_offender_age?: number | string;
  sex_offender_charges?: string;
  sex_offender_is_predator?: string | boolean | number;
  sex_offender_is_absconder?: string | boolean | number;
  sex_offender_image_url?: string;
}

interface CrimeIncidentData {
  incident_offense?: string;
  incident_offense_description?: string;
  incident_address?: string;
  incident_date?: string | number | Date;
  incident_offense_detail_description?: string;
  incident_offense_crime_against?: string;
  incident_code?: string;
}

type MarkerData = unknown;

interface GoogleMapMarker {
  lat: number;
  lng: number;
  color?: string;
  type?: "offender" | "crime";
  data?: MarkerData; // Original data for tooltip
}

interface GoogleMapProps {
  address: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  markers?: GoogleMapMarker[];
  className?: string;
}

type GMap = google.maps.Map;
type GMarker = google.maps.Marker;
type GInfoWindow = google.maps.InfoWindow;

interface GoogleMapsAPI {
  maps?: typeof google.maps;
}

const hasGoogleMaps = (
  win: Window
): win is Window & { google: { maps: NonNullable<GoogleMapsAPI["maps"]> } } => {
  const googleAny = (win as Window & { google?: GoogleMapsAPI }).google;
  return (
    typeof googleAny !== "undefined" &&
    typeof googleAny.maps !== "undefined" &&
    // Ensure core constructors exist (prevents "Map is not a constructor" during partial loads)
    typeof googleAny.maps.Map === "function"
  );
};

// Helper function to create InfoWindow content
const createInfoWindowContent = (
  type: "offender" | "crime" | undefined,
  data: MarkerData
): string => {
  if (!data) return "<div>No details available</div>";

  if (type === "offender") {
    const offender = data as Partial<OffenderData>;
    const name =
      offender.sex_offender_name ||
      `${offender.sex_offender_first_name || ""} ${offender.sex_offender_middle_name || ""} ${offender.sex_offender_last_name || ""}`.trim() ||
      "Unknown";
    const address =
      offender.sex_offender_address_line1 ||
      offender.sex_offender_location ||
      "Address not available";
    const age = offender.sex_offender_age ? `Age: ${offender.sex_offender_age}` : "";
    const charges = offender.sex_offender_charges || "Charges not available";
    const isPredator =
      offender.sex_offender_is_predator === "true" || offender.sex_offender_is_predator === "1";
    const isAbsconder =
      offender.sex_offender_is_absconder === "true" || offender.sex_offender_is_absconder === "1";
    const imageUrl = offender.sex_offender_image_url || "";

    return `
      <div style="padding: 12px; max-width: 320px; font-family: Arial, sans-serif; display: flex; gap: 10px;">
        ${
          imageUrl
            ? `<div style="flex-shrink:0;">
               <img src="${imageUrl}"
                    alt="Offender photo"
                    style="width:72px;height:72px;object-fit:cover;border-radius:999px;border:2px solid #eab308;" />
             </div>`
            : ""
        }
        <div style="flex:1; min-width:0;">
          <h3 style="margin: 0 0 6px 0; color: #eab308; font-size: 16px; font-weight: bold;">
            ⚠️ Sex Offender
          </h3>
          <div style="margin-bottom: 6px; font-size: 13px;">
            <strong>Name:</strong> ${name}<br/>
            ${age ? `<strong>${age}</strong><br/>` : ""}
            <strong>Address:</strong> ${address}
          </div>
          <div style="margin-bottom: 6px; font-size: 13px;">
            <strong>Charges:</strong> ${charges}
          </div>
          ${isPredator ? '<div style="color: #dc2626; font-weight: bold; margin-top: 4px; font-size: 13px;">⚠️ Predator</div>' : ""}
          ${isAbsconder ? '<div style="color: #dc2626; font-weight: bold; margin-top: 4px; font-size: 13px;">🚨 Absconder</div>' : ""}
        </div>
      </div>
    `;
  } else if (type === "crime") {
    const incident = data as Partial<CrimeIncidentData>;
    const offense =
      incident.incident_offense || incident.incident_offense_description || "Unknown offense";
    const address = incident.incident_address || "Address not available";
    const date = incident.incident_date
      ? new Date(incident.incident_date).toLocaleDateString()
      : "Date not available";
    const description =
      incident.incident_offense_detail_description || incident.incident_offense_description || "";
    const crimeAgainst = incident.incident_offense_crime_against || "";

    return `
      <div style="padding: 12px; max-width: 300px; font-family: Arial, sans-serif;">
        <h3 style="margin: 0 0 8px 0; color: #ef4444; font-size: 16px; font-weight: bold;">
          🚨 Crime Incident
        </h3>
        <div style="margin-bottom: 8px;">
          <strong>Offense:</strong> ${offense}<br/>
          <strong>Date:</strong> ${date}<br/>
          <strong>Location:</strong> ${address}
        </div>
        ${description ? `<div style="margin-bottom: 8px;"><strong>Description:</strong> ${description}</div>` : ""}
        ${crimeAgainst ? `<div style="margin-bottom: 8px;"><strong>Crime Against:</strong> ${crimeAgainst}</div>` : ""}
        ${incident.incident_code ? `<div style="font-size: 12px; color: #666; margin-top: 8px;">Code: ${incident.incident_code}</div>` : ""}
      </div>
    `;
  }

  return "<div>No details available</div>";
};

export const GoogleMap = ({
  address,
  center,
  zoom = 14,
  markers = [],
  className = "",
}: GoogleMapProps) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<GMap | null>(null);
  const mapMarkersRef = useRef<GMarker[]>([]);
  const infoWindowRef = useRef<GInfoWindow | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const userHasZoomedRef = useRef(false);

  const homeIconPath = "M12 3L2 11h3v8h6v-5h2v5h6v-8h3L12 3z";
  // Location pin icon path (standard Google Maps pin shape)
  const locationPinPath =
    "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z";

  // Load script
  useEffect(() => {
    const apiKey =
      import.meta.env.VITE_GOOGLE_MAPS_API_KEY || import.meta.env.VITE_GOOGLE_MAPS_API || "";

    if (!apiKey) return;

    if (hasGoogleMaps(window)) {
      setIsLoaded(true);
      return;
    }

    const existingScript = document.querySelector(`script[src*="maps.googleapis.com"]`);
    if (existingScript) {
      existingScript.addEventListener("load", () => setIsLoaded(true));
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Init map
  useEffect(() => {
    if (!isLoaded || !mapRef.current || !hasGoogleMaps(window) || map) return;

    const googleMaps = window.google.maps;

    const defaultCenter = center || { lat: 42.1486, lng: -72.608 };

    const mapInstance = new googleMaps.Map(mapRef.current, {
      center: defaultCenter,
      zoom,
      gestureHandling: "greedy",
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      // Keep zoom controls visible even when consumers overlay UI at the bottom of the map
      zoomControl: true,
      zoomControlOptions: {
        position: googleMaps.ControlPosition.RIGHT_TOP,
      },
    });

    setMap(mapInstance);

    googleMaps.event.addListener(mapInstance, "zoom_changed", () => {
      userHasZoomedRef.current = true;
    });

    googleMaps.event.addListener(mapInstance, "dragend", () => {
      userHasZoomedRef.current = true;
    });
  }, [isLoaded, map, center, zoom]);

  // Geocode address
  useEffect(() => {
    if (!map || center || !address || !hasGoogleMaps(window)) return;

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === "OK" && results && results[0] && results[0].geometry) {
        map.setCenter(results[0].geometry.location);
      }
    });
  }, [map, address, center]);

  // Property marker
  useEffect(() => {
    if (!map || !hasGoogleMaps(window)) return;

    const googleMaps = window.google.maps;

    const marker = new googleMaps.Marker({
      position: center || { lat: 42.1486, lng: -72.608 },
      map,
      icon: {
        path: homeIconPath,
        scale: 2.5,
        fillColor: "#10b981",
        fillOpacity: 1,
        strokeColor: "#065f46",
        strokeWeight: 1.5,
        anchor: new googleMaps.Point(12, 12),
      },
    });

    return () => marker.setMap(null);
  }, [map, center]);

  // Markers + fitBounds
  useEffect(() => {
    if (!map || !hasGoogleMaps(window) || !markers?.length) {
      // Clear markers if no markers provided
      if (map && mapMarkersRef.current.length > 0) {
        mapMarkersRef.current.forEach((m) => m.setMap(null));
        mapMarkersRef.current = [];
      }
      return;
    }

    const googleMaps = window.google.maps;

    // Clear old markers
    mapMarkersRef.current.forEach((m) => m.setMap(null));
    const newMarkers: GMarker[] = [];

    // Reset zoom tracking when new markers are added
    userHasZoomedRef.current = false;

    const bounds = new googleMaps.LatLngBounds();

    const centerPos = center || { lat: 42.1486, lng: -72.608 };
    bounds.extend(centerPos);

    markers.forEach((m, index) => {
      const position = {
        lat: Number(m.lat),
        lng: Number(m.lng),
      };

      // Validate position
      if (isNaN(position.lat) || isNaN(position.lng)) {
        console.warn("[GoogleMap] Invalid marker position:", m);
        return;
      }

      const color = m.color || (m.type === "offender" ? "#eab308" : "#ef4444");

      bounds.extend(position);

      // Create marker with location pin icon
      const marker = new googleMaps.Marker({
        position,
        map,
        icon: {
          path: locationPinPath,
          scale: 1.2,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
          anchor: new googleMaps.Point(12, 24), // Pin point at bottom center
        },
        title: `${m.type || "marker"} ${index + 1}`,
        optimized: false,
        visible: true,
      });

      // Create InfoWindow content based on type
      const infoContent = createInfoWindowContent(m.type, m.data);

      // Create InfoWindow (reuse single instance)
      if (!infoWindowRef.current) {
        infoWindowRef.current = new googleMaps.InfoWindow();
      }

      // Add click listener to show InfoWindow
      googleMaps.event.addListener(marker, "click", () => {
        const infoWindow = infoWindowRef.current;
        if (!infoWindow) return;
        infoWindow.setContent(infoContent);
        infoWindow.open(map, marker);
      });

      // Verify marker was created and added to map
      if (index === 0) {
        console.log("[GoogleMap] First marker created:", {
          position,
          color,
          map: marker.getMap() !== null,
          visible: marker.getVisible(),
        });
      }

      newMarkers.push(marker);
    });

    console.log("[GoogleMap] Created markers:", newMarkers.length);
    console.log("[GoogleMap] Bounds:", {
      north: bounds.getNorthEast()?.lat(),
      south: bounds.getSouthWest()?.lat(),
      east: bounds.getNorthEast()?.lng(),
      west: bounds.getSouthWest()?.lng(),
    });
    console.log(
      "[GoogleMap] First marker position:",
      newMarkers[0]?.getPosition()?.lat(),
      newMarkers[0]?.getPosition()?.lng()
    );
    console.log("[GoogleMap] Map center:", map.getCenter()?.lat(), map.getCenter()?.lng());
    console.log("[GoogleMap] Map zoom:", map.getZoom());

    mapMarkersRef.current = newMarkers;

    // Fit bounds to show all markers and center
    // Use setTimeout to ensure markers are rendered first
    setTimeout(() => {
      if (map && bounds && newMarkers.length > 0 && !userHasZoomedRef.current) {
        try {
          // Check if bounds are valid
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();

          if (ne && sw) {
            console.log("[GoogleMap] Fitting bounds...");
            map.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50,
            });

            // Limit max zoom to prevent zooming in too much
            const listener = googleMaps.event.addListenerOnce(map, "bounds_changed", () => {
              const currentZoom = map.getZoom();
              console.log("[GoogleMap] After fitBounds, zoom:", currentZoom);
              if (currentZoom && currentZoom > 16) {
                map.setZoom(16);
              }
              googleMaps.event.removeListener(listener);
            });
          } else {
            console.warn("[GoogleMap] Invalid bounds, cannot fit");
          }
        } catch (error) {
          console.error("[GoogleMap] Error fitting bounds:", error);
        }
      } else {
        console.warn("[GoogleMap] Cannot fit bounds:", {
          hasMap: !!map,
          hasBounds: !!bounds,
          markersCount: newMarkers.length,
          userHasZoomed: userHasZoomedRef.current,
        });
      }
    }, 200);

    return () => {
      // Close InfoWindow if open
      infoWindowRef.current?.close();
      // Remove all markers
      newMarkers.forEach((m) => m.setMap(null));
    };
  }, [map, markers, center]);

  return (
    <div className={`${styles.mapContainer} ${className}`}>
      {!isLoaded && <div>Loading map...</div>}
      <div ref={mapRef} className={styles.map} />
    </div>
  );
};
